import { z } from 'zod';

/**
 * The number printed on the artefact a visitor hands over, and the shape a
 * lookup will accept before it makes a request.
 *
 * `T03-000871` is the terminal's prefix, a hyphen, and the counter value padded
 * to six (FR-307). The API's `parseTicketNumber` reads it back and refuses
 * anything else with `VALIDATION_FAILED` — 400, non-retryable — before the value
 * reaches PostgreSQL. **Everything below is that same rule restated on this side
 * of the wire**, so somebody who has misread a smudged thermal print gets a
 * sentence under the field instead of a round trip that comes back as a code.
 *
 * ## It restates the server's rule and does not add to it
 *
 * The temptation on a field like this is to insist on the *printed* form —
 * exactly six digits, a prefix that looks like `T` and two figures. Both would
 * refuse tickets the API accepts:
 *
 * - **Padding is presentation.** `formatTicketNumber` pads to six with
 *   `padStart`, which widens rather than truncates, so a till past 999,999
 *   prints seven digits. `T03-871`, `T03-000871` and `T03-0000000871` are one
 *   ticket, and a field insisting on six would stop reading its own printer's
 *   output at the one boundary where it matters.
 * - **A prefix is `terminals.prefix`, which is unique and otherwise
 *   unconstrained.** A park that names a till `GATE-N` is doing nothing the
 *   database forbids, which is why the separator below is the *last* hyphen and
 *   not the first.
 *
 * So this refuses exactly four things, because those are the four the API
 * refuses: no separator (`WOMBAT`), an empty prefix (`-000001`), an empty or
 * non-digit value (`T01-`), and a value outside a counter's range.
 *
 * ## Case is the server's to fix, and it does
 *
 * Prefixes are stored uppercase — `TerminalsService.create` uppercases before
 * the row is written — and `parseTicketNumber` uppercases what it is given. So
 * `t03-000871` finds the same ticket as `T03-000871`, and this file deliberately
 * does **not** uppercase on the way out: the value that was typed is the value
 * that is sent, the answer comes back carrying the server's own canonical
 * `number`, and there is no place for this app to have an opinion about case
 * that could ever disagree with the one that decides.
 */

/**
 * The separator, and the **last** one in the string rather than the first, for
 * the reason the head of this file gives — `GATE-N-000871` is prefix `GATE-N`
 * and value `000871`, because the padded value can never contain a hyphen.
 */
const SEPARATOR = '-';

/**
 * The value half, exactly: one or more ASCII digits and nothing else.
 *
 * Deliberately a pattern rather than a `try { BigInt(x) }`. `BigInt(' 871')` is
 * `871n`, `BigInt('0x371')` is `883n` and `BigInt('')` is `0n` — each accepted by
 * the bare conversion, and each a different number from the one on the paper. A
 * leading `+` or `-` is refused by the same anchor, which is what keeps
 * `-000001` a malformed shape rather than a negative counter value.
 */
const DIGITS = /^[0-9]+$/;

/**
 * What a counter can have printed: `1` to the largest value PostgreSQL's
 * `bigint` holds.
 *
 * Restated from the API's `MIN_COUNTER_VALUE` and `MAX_COUNTER_VALUE` because
 * there is nothing to import — the published document carries no bound for this
 * path parameter, only the prose that names it. Zero means nothing was printed,
 * and above `int8` the API refuses the conversion rather than letting the driver
 * raise something that leaves as a *retryable* internal error.
 *
 * `BigInt` and not `Number`: a counter value near the top of that range is past
 * `Number.MAX_SAFE_INTEGER`, so comparing as a number would accept values above
 * the bound and refuse values below it, both silently.
 */
const MIN_COUNTER_VALUE = 1n;
const MAX_COUNTER_VALUE = 9223372036854775807n;

/** The shape, as a person reads it. Used in copy and as the field's example. */
export const TICKET_NUMBER_EXAMPLE = 'T03-000871';

export const TICKET_NUMBER_REQUIRED = 'Enter the number printed on the ticket.';

/**
 * One message for every malformed shape, matching the API's own single
 * constraint string.
 *
 * Not three messages for the three ways the shape can be wrong. The remedy is
 * identical in every case — read the number off the ticket again — and a refusal
 * that distinguished "well formed but too large" from "not a number at all"
 * would be a small oracle about what this park's counters have reached, which is
 * the reason the API words it once too.
 */
export const TICKET_NUMBER_MALFORMED = `That is not the shape of a ticket number. One reads ${TICKET_NUMBER_EXAMPLE} — the terminal's prefix, a hyphen, and the number printed on the ticket.`;

/**
 * Whether a value is shaped like something a terminal has printed.
 *
 * Answers about the *shape* only. Whether a ticket carrying it exists, and
 * whether the caller may see it, are one question with one answer and the server
 * is the only thing that can give it.
 */
export function isPrintedTicketNumber(value: string): boolean {
  const separator = value.lastIndexOf(SEPARATOR);

  // `WOMBAT` — no separator at all. `-000001` — a separator at 0, so the prefix
  // is empty. `T01-` — a separator at the end, so the value is. All three are
  // one condition and one refusal, because to the person who typed it they are
  // one fault: that is not the shape of a ticket number.
  if (separator <= 0 || separator === value.length - 1) return false;

  const printed = value.slice(separator + 1);

  if (!DIGITS.test(printed)) return false;

  const counterValue = BigInt(printed);

  return counterValue >= MIN_COUNTER_VALUE && counterValue <= MAX_COUNTER_VALUE;
}

/**
 * A ticket number somebody is looking up.
 *
 * Trimmed before it is judged, so a value pasted with a trailing space is a
 * space and not a malformed number — and so the value the form submits is the
 * value it validated.
 */
export const ticketNumberSchema = z
  .string()
  .trim()
  .min(1, TICKET_NUMBER_REQUIRED)
  .refine(isPrintedTicketNumber, TICKET_NUMBER_MALFORMED);
